var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Checkbox, Collapse, FormControlLabel, Link, Stack, styled } from '@mui/material';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useTerminologyTranslation } from '../../app/terminology';
import { Autocomplete, ErrorMessageBox, FormField, InputLabel, UnControlledFormField } from '../../common';
import { SelectOrCustomField } from '../../common/selectOrCustomField';
import { CUSTOM_FACTOR_VALUE, PREDEFINED_SHRINKAGE_FACTORS, SHRINKAGE_CATEGORY_IN_OFFICE, SHRINKAGE_CATEGORY_OUT_OF_OFFICE, SHRINKAGE_FACTOR_I18N_KEYS, } from '../../orgConfigWizard/shrinkage/shrinkageConstants';
import { ALL_LOCATIONS_ID, MAX_TEXT_LENGTH } from '../constants';
import { WizardContext } from '../context';
import { useQueueAPI } from '../queue/selectors';
import { useIsPlanLocked, useLocationOptions, useMultiLangTranslations, useQueueLocations, useWizardParams, } from '../selectors';
import { SHRINKAGE_SUBCATEGORY_TRANS_KEYS, SHRINKAGE_SUBCATEGORY_TYPES, ShrinkageSubcategory } from './constants';
import { useAddShrinkageAPI, useShrinkagePlanOptionsAPI } from './selectors';
var initialCategory = SHRINKAGE_CATEGORY_OUT_OF_OFFICE;
var RootBox = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        marginTop: theme.spacing(6),
    });
});
var FormContainer = styled(Box)(function (_a) {
    var theme = _a.theme;
    return ({
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(6),
        marginTop: theme.spacing(6),
        backgroundColor: theme.palette.grey[200],
    });
});
var FieldWrapper = styled(Box)({
    minWidth: 180,
    flex: '1 1 180px',
});
var CheckboxWrapper = styled(Box)({
    minWidth: 200,
    flex: '0 0 auto',
});
var CheckboxStack = styled(Stack)(function (_a) {
    var theme = _a.theme;
    return ({
        marginTop: theme.spacing(0.5),
    });
});
var validateForm = yup.object({
    factor: yup.string().required(),
    customName: yup.string().when('factor', function (_a, schema) {
        var factor = _a[0];
        return factor === CUSTOM_FACTOR_VALUE
            ? schema.normalizeSpace().required().max(MAX_TEXT_LENGTH)
            : schema.notRequired();
    }),
    subcategory: yup.string().nullable(),
    customSubcategory: yup.string().nullable(),
    queueLocationId: yup.number().nullable(),
});
var initialFactor = PREDEFINED_SHRINKAGE_FACTORS[initialCategory][0];
var getTrimmedUniqueNames = function (values) {
    return Array.from(new Set(values.map(function (value) { return value === null || value === void 0 ? void 0 : value.trim(); }).filter(function (value) { return !!value; })));
};
var getOptionsWithCustomLast = function (predefinedOptions, customOptions) {
    var customOption = predefinedOptions.at(-1);
    return customOption
        ? __spreadArray(__spreadArray(__spreadArray([], predefinedOptions.slice(0, -1), true), customOptions, true), [customOption], false) : __spreadArray(__spreadArray([], predefinedOptions, true), customOptions, true);
};
var getCustomSubcategoryOptions = function (planOptions, translatedSubcategoryNames) {
    return getTrimmedUniqueNames((planOptions !== null && planOptions !== void 0 ? planOptions : []).map(function (option) { return option.shrinkageSubcategory; }))
        .filter(function (subcategory) {
        return !SHRINKAGE_SUBCATEGORY_TYPES.includes(subcategory) &&
            !translatedSubcategoryNames.has(subcategory) &&
            subcategory !== ShrinkageSubcategory.Custom;
    })
        .sort(function (left, right) { return left.localeCompare(right); })
        .map(function (subcategory) { return ({
        id: subcategory,
        name: subcategory,
    }); });
};
var getCustomFactorOptions = function (planOptions, category, predefinedFactors, translatedPredefinedNames) {
    return getTrimmedUniqueNames((planOptions !== null && planOptions !== void 0 ? planOptions : []).filter(function (option) { return option.shrinkageCategory === category; }).map(function (option) { return option.name; }))
        .filter(function (name) {
        return !predefinedFactors.includes(name) && !translatedPredefinedNames.has(name) && name !== CUSTOM_FACTOR_VALUE;
    })
        .sort(function (left, right) { return left.localeCompare(right); })
        .map(function (name) { return ({ id: name, name: name }); });
};
export var AddShrinkageFactor = function () {
    var _a;
    var _b = useTerminologyTranslation(['wizard', 'translation']), t = _b.t, i18n = _b.i18n;
    var translate = useCallback(function (key) { return String(t(key, { defaultValue: key })); }, [t]);
    var translateShrinkageFactor = useCallback(function (factorName) { var _a; return translate("shrinkage.factors.".concat((_a = SHRINKAGE_FACTOR_I18N_KEYS[factorName]) !== null && _a !== void 0 ? _a : factorName)); }, [translate]);
    var translateSubcategory = useCallback(function (subcategoryName) { var _a; return translate((_a = SHRINKAGE_SUBCATEGORY_TRANS_KEYS[subcategoryName]) !== null && _a !== void 0 ? _a : subcategoryName); }, [translate]);
    var queueId = useWizardParams().queueId;
    var _c = useAddShrinkageAPI(), addShrinkageData = _c[0], addShrinkage = _c[1], clearAddShrinkage = _c[2];
    var _d = useShrinkagePlanOptionsAPI(), planOptionsData = _d[0], fetchPlanOptions = _d[1];
    var _e = useQueueAPI(), fetchQueue = _e[1];
    var isPlanLocked = useIsPlanLocked();
    var _f = useContext(WizardContext), handleSaveWithoutValidation = _f.handleSaveWithoutValidation, isDirty = _f.methods.formState.isDirty;
    var locations = useQueueLocations();
    var locationOptions = useLocationOptions();
    var hasLocations = !!locations.length;
    var initialValues = {
        factor: initialFactor,
        customName: '',
        subcategory: '',
        customSubcategory: '',
        queueLocationId: hasLocations ? ALL_LOCATIONS_ID : null,
    };
    var _g = useState(false), open = _g[0], setOpen = _g[1];
    var _h = useState(initialCategory), category = _h[0], setCategory = _h[1];
    var _j = useState(true), planned = _j[0], setPlanned = _j[1];
    var _k = useState(false), unplanned = _k[0], setUnplanned = _k[1];
    var subcatI18nKeys = useMemo(function () { return SHRINKAGE_SUBCATEGORY_TYPES.map(function (type) { return SHRINKAGE_SUBCATEGORY_TRANS_KEYS[type]; }); }, []);
    var translatedSubcategoryNames = useMultiLangTranslations(i18n, subcatI18nKeys, 'wizard');
    var subcategoryOptions = useMemo(function () {
        var predefinedOptions = SHRINKAGE_SUBCATEGORY_TYPES.map(function (type) { return ({
            id: type,
            name: translateSubcategory(type),
        }); });
        var customOptions = getCustomSubcategoryOptions(planOptionsData.data, translatedSubcategoryNames);
        return getOptionsWithCustomLast(predefinedOptions, customOptions);
    }, [planOptionsData.data, translateSubcategory, translatedSubcategoryNames]);
    var _l = useForm({
        defaultValues: initialValues,
        resolver: yupResolver(validateForm),
    }), control = _l.control, watch = _l.watch, setValue = _l.setValue, resetFactorForm = _l.reset, handleSubmit = _l.handleSubmit;
    var _m = watch(), factor = _m.factor, customName = _m.customName, subcategory = _m.subcategory, customSubcategory = _m.customSubcategory, queueLocationId = _m.queueLocationId;
    var factorI18nKeys = useMemo(function () {
        return PREDEFINED_SHRINKAGE_FACTORS[category]
            .filter(function (k) { return k !== CUSTOM_FACTOR_VALUE; })
            .map(function (k) { return "shrinkage.factors.".concat(SHRINKAGE_FACTOR_I18N_KEYS[k]); });
    }, [category]);
    var allTranslatedPredefinedFactorNames = useMultiLangTranslations(i18n, factorI18nKeys, 'wizard');
    var factorOptions = useMemo(function () {
        var predefinedFactors = PREDEFINED_SHRINKAGE_FACTORS[category];
        var customFactorOptions = getCustomFactorOptions(planOptionsData.data, category, predefinedFactors, allTranslatedPredefinedFactorNames);
        return __spreadArray(__spreadArray(__spreadArray([], predefinedFactors
            .filter(function (key) { return key !== CUSTOM_FACTOR_VALUE; })
            .map(function (key) { return ({
            id: key,
            name: translateShrinkageFactor(key),
        }); }), true), customFactorOptions, true), [
            {
                id: CUSTOM_FACTOR_VALUE,
                name: translateShrinkageFactor(CUSTOM_FACTOR_VALUE),
            },
        ], false);
    }, [category, planOptionsData.data, translateShrinkageFactor, allTranslatedPredefinedFactorNames]);
    var categoryOptions = useMemo(function () { return [
        { id: SHRINKAGE_CATEGORY_OUT_OF_OFFICE, name: t('wizard:shrinkage.category.out_of_office') },
        { id: SHRINKAGE_CATEGORY_IN_OFFICE, name: t('wizard:shrinkage.category.in_office') },
    ]; }, [t]);
    var resetForm = function () {
        setCategory(initialCategory);
        resetFactorForm(initialValues);
        setPlanned(true);
        setUnplanned(false);
    };
    useEffect(function () {
        if (addShrinkageData.data) {
            fetchQueue();
            fetchPlanOptions();
            resetForm();
        }
        return clearAddShrinkage;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addShrinkageData.data, clearAddShrinkage, fetchPlanOptions, fetchQueue]);
    useEffect(function () {
        if (open) {
            fetchPlanOptions();
        }
    }, [fetchPlanOptions, open]);
    var isCustom = factor === CUSTOM_FACTOR_VALUE;
    var isCustomSubcategory = subcategory === ShrinkageSubcategory.Custom;
    var predefinedFactorKeys = PREDEFINED_SHRINKAGE_FACTORS[category];
    var isPredefinedFactor = predefinedFactorKeys.includes(factor);
    var subcategoryValue = subcategory !== null && subcategory !== void 0 ? subcategory : '';
    var isPredefinedSubcategory = SHRINKAGE_SUBCATEGORY_TYPES.includes(subcategoryValue);
    var trimmedCustomName = (_a = customName === null || customName === void 0 ? void 0 : customName.trim()) !== null && _a !== void 0 ? _a : '';
    var trimmedCustomSubcategory = customSubcategory === null || customSubcategory === void 0 ? void 0 : customSubcategory.trim();
    var getShrinkageName = function () {
        if (isCustom) {
            return trimmedCustomName;
        }
        if (isPredefinedFactor) {
            return translateShrinkageFactor(factor);
        }
        return factor;
    };
    var getShrinkageSubcategoryName = function () {
        if (isCustomSubcategory) {
            return trimmedCustomSubcategory || null;
        }
        if (subcategoryValue && isPredefinedSubcategory) {
            return translateSubcategory(subcategoryValue);
        }
        if (subcategoryValue)
            return subcategoryValue;
        return null;
    };
    var name = getShrinkageName();
    var subCategoryName = getShrinkageSubcategoryName();
    var shrinkageTypes = [planned ? 'planned' : undefined, unplanned ? 'unplanned' : undefined].filter(Boolean);
    var canSave = Boolean(name) && shrinkageTypes.length > 0;
    var handleCategoryChange = function (value) {
        var _a;
        var nextCategory = (_a = value) !== null && _a !== void 0 ? _a : initialCategory;
        setCategory(nextCategory);
        var nextFactor = PREDEFINED_SHRINKAGE_FACTORS[nextCategory][0];
        setValue('factor', nextFactor);
        setValue('customName', '');
    };
    var handleAddShrinkage = function () {
        if (!canSave)
            return;
        var payload = {
            queueId: queueId,
            name: name,
            shrinkageCategory: category,
            shrinkageSubcategory: subCategoryName !== null && subCategoryName !== void 0 ? subCategoryName : null,
            shrinkageTypes: shrinkageTypes,
            queueLocationId: queueLocationId !== null && queueLocationId !== void 0 ? queueLocationId : null,
        };
        var action = function () { return addShrinkage(payload); };
        if (isDirty) {
            handleSaveWithoutValidation('submit', action);
            return;
        }
        action();
    };
    return (_jsxs(RootBox, { children: [_jsx(Link, __assign({ component: "button", disabled: isPlanLocked, onClick: function () { return setOpen(function (prev) { return !prev; }); }, "data-testid": "addShrinkageLink" }, { children: t('wizard:shrinkage.addAnotherShrinkageFactor') })), _jsx(Collapse, __assign({ in: open, unmountOnExit: true }, { children: _jsxs(FormContainer, { children: [_jsx(ErrorMessageBox, { errors: [addShrinkageData.error], scrollTop: false }), _jsxs(Stack, __assign({ direction: "row", gap: 3, sx: { alignItems: 'flex-start', flexWrap: 'wrap' } }, { children: [_jsx(FieldWrapper, { children: _jsx(UnControlledFormField, __assign({ required: true, label: t('wizard:shrinkage.type') }, { children: _jsx(Autocomplete, { options: categoryOptions, value: category, getOptionValue: function (o) { return o.id; }, onChange: handleCategoryChange, disabled: isPlanLocked }) })) }), _jsxs(FieldWrapper, { children: [_jsx(InputLabel, __assign({ required: true }, { children: t('wizard:shrinkage.shrinkageFactor') })), _jsx(SelectOrCustomField, { required: true, isCustom: isCustom, control: control, name: "factor", customName: "customName", options: factorOptions, defaultValue: PREDEFINED_SHRINKAGE_FACTORS[category][0], placeholder: t('translation:layout.pleaseSelect'), customPlaceholder: t('wizard:shrinkage.enterCustomFactor'), customValue: customName, onCustomChange: function (value) { return setValue('customName', value); }, onSelectChange: function (value) { return setValue('factor', value !== null && value !== void 0 ? value : PREDEFINED_SHRINKAGE_FACTORS[category][0]); }, maxLength: MAX_TEXT_LENGTH, disabled: isPlanLocked })] }), _jsxs(FieldWrapper, { children: [_jsx(InputLabel, { children: t('wizard:shrinkage.subcategory') }), _jsx(SelectOrCustomField, { required: true, isCustom: isCustomSubcategory, control: control, name: "subcategory", customName: "customSubcategory", options: subcategoryOptions, disableClearable: false, defaultValue: '', placeholder: t('translation:layout.pleaseSelect'), customPlaceholder: t('wizard:shrinkage.enterCustomSubcategory'), customValue: customSubcategory !== null && customSubcategory !== void 0 ? customSubcategory : '', onCustomChange: function (value) { return setValue('customSubcategory', value); }, onSelectChange: function (value) { return setValue('subcategory', value); }, maxLength: MAX_TEXT_LENGTH, disabled: isPlanLocked })] }), hasLocations && (_jsxs(FieldWrapper, { children: [_jsx(InputLabel, { children: t('shrinkage.locations') }), _jsx(FormField, __assign({ required: true, control: control, name: "queueLocationId" }, { children: _jsx(Autocomplete, { options: locationOptions, placeholder: t('translation:layout.pleaseSelect'), size: "small", disabled: isPlanLocked }) }))] })), _jsxs(CheckboxWrapper, { children: [_jsx(InputLabel, __assign({ sx: { visibility: 'hidden' } }, { children: '\u00A0' })), _jsxs(CheckboxStack, __assign({ direction: "row", spacing: 1 }, { children: [_jsx(FormControlLabel, { control: _jsx(Checkbox, { disabled: isPlanLocked, checked: planned, onChange: function (e) { return setPlanned(e.target.checked); }, size: "small" }), label: t('wizard:shrinkage.planned') }), _jsx(FormControlLabel, { control: _jsx(Checkbox, { disabled: isPlanLocked, checked: unplanned, onChange: function (e) { return setUnplanned(e.target.checked); }, size: "small" }), label: t('wizard:shrinkage.unplanned') })] }))] }), _jsxs(Box, __assign({ sx: { ml: 'auto' } }, { children: [_jsx(InputLabel, __assign({ sx: { visibility: 'hidden' } }, { children: '\u00A0' })), _jsx(LoadingButton, __assign({ variant: "outlined", size: "medium", loading: !!addShrinkageData.requesting, onClick: handleSubmit(handleAddShrinkage), disabled: (!planned && !unplanned) || isPlanLocked }, { children: t('translation:action.add') }))] }))] }))] }) }))] }));
};
